import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { ACCENTS, type AccentName } from "../../lib/accents";
import { IconChip } from "./IconChip";

export interface SectionCardProps {
  icon: LucideIcon;
  title: string;
  accent: AccentName;
  /** One calm line under the title. */
  description?: ReactNode;
  /** Right-aligned header slot, e.g. an "Add" button. */
  actions?: ReactNode;
  /** Landmark element. `section` (default) is semantic; `div` opts out. */
  as?: "section" | "div";
  /**
   * `wash` (default): the section sits on a soft accent gradient that fades
   * to translucent white — an area, not a box. `open`: no surface at all,
   * for a section that already sits inside a card or dialog.
   */
  surface?: "wash" | "open";
  className?: string;
  children: ReactNode;
}

/**
 * A content section in the landing page's voice (docs/v2-core/STYLE.md):
 * a round accent chip and a Fredoka title over the content, on a soft
 * gradient wash — no tinted header band, no divider, no hard border. The
 * wash never goes past a -100 tint at 70%, so body text stays AA-readable
 * in neutral-700/800/900.
 *
 * The name is historical; it is no longer a card.
 */
export function SectionCard({
  icon,
  title,
  accent,
  description,
  actions,
  as = "section",
  surface = "wash",
  className,
  children,
}: SectionCardProps) {
  const Comp = as;
  return (
    <Comp
      className={cn(
        "relative",
        surface === "wash" && cn("rounded-[2rem] p-4 ring-1 ring-white/70 sm:p-7", ACCENTS[accent].wash),
        className,
      )}
    >
      {/* The title asks for 11rem before it shrinks, so on a phone the
          actions drop to their own line instead of crushing a long title. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-3 sm:gap-x-4">
        <IconChip icon={icon} accent={accent} size="md" />
        <div className="min-w-0 flex-[1_1_11rem]">
          <h2 className="font-display text-xl font-semibold leading-tight text-neutral-900 sm:text-[1.4rem]">
            {title}
          </h2>
          {description && <p className="mt-0.5 text-sm text-neutral-600">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div className="mt-5 sm:mt-6">{children}</div>
    </Comp>
  );
}
